import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "../utils/apiError";
import {
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineSearch,
  HiOutlineX,
  HiOutlineChevronLeft,
  HiOutlineLocationMarker,
  HiOutlineExclamation,
  HiOutlineUpload,
  HiOutlineDownload,
  HiOutlineDocumentText,
} from "react-icons/hi";
import {
  fetchMasterData,
  fetchBlockById,
  fetchDistrictById,
  createBlock,
  updateBlock,
  deleteBlock,
  bulkAction,
  bulkUpload,
} from "../services/masterDataApi";
import SearchableDropdown, { scopedStatesFetchFn } from "../components/SearchableDropdown";
import Toast from "../components/Toast";
import Pagination from "../components/Pagination";
import FilterDropdown from "../components/FilterDropdown";
import { useDebounce } from "../hooks/useDebounce";
import { useUserRole } from "../hooks/useUserRole";

function Blocks() {
  const { canManageBlocks, role } = useUserRole();
  const [data, setData] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  /* ── views: "list" | "form" — backed by the URL so a refresh in edit mode
     reopens the same block instead of dropping back to the list ── */
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") || "list";
  const urlEditId = searchParams.get("edit");
  const setView = useCallback((v, params = {}) => {
    if (v === "list") {
      setSearchParams({});
    } else if (v === "form" && params.edit) {
      setSearchParams({ view: "form", edit: params.edit });
    } else {
      setSearchParams({ view: v });
    }
  }, [setSearchParams]);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState("");
  const [formStateId, setFormStateId] = useState("");
  const [formDistrictId, setFormDistrictId] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const selectAllRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Filter state
  const [filters, setFilters] = useState({ state_id: "", district_id: "" });
  const [draftFilters, setDraftFilters] = useState({ state_id: "", district_id: "" });
  // Full district list per state for the Filters panel, fetched on demand (bypasses the
  // page_size=100 server cap that the global `districts` prefetch below is subject to).
  const [filterDistrictsByState, setFilterDistrictsByState] = useState({});

  // Update indeterminate state of select-all checkbox
  useEffect(() => {
    if (selectAllRef.current) {
      const pageIds = data.map((item) => item.id);
      const selectedInView = selectedIds.filter((id) => pageIds.includes(id));
      selectAllRef.current.indeterminate =
        selectedInView.length > 0 && selectedInView.length < pageIds.length;
    }
  }, [selectedIds, data]);

  const loadData = useCallback(async (page = currentPage, searchQuery = debouncedSearch, activeFilters = filters) => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, search: searchQuery };
      if (activeFilters.state_id) params.state_id = activeFilters.state_id;
      if (activeFilters.district_id) params.district_id = activeFilters.district_id;
      // Rows already carry district/state names — the lookup lists are
      // loaded once below, not on every page/search/filter.
      const blocksResult = await fetchMasterData("blocks", params);
      setData(blocksResult.data);
      setTotalCount(blocksResult.total_count);
      setTotalPages(blocksResult.total_pages);
      setCurrentPage(blocksResult.current_page);
    } catch (err) {
      console.error("Failed to load blocks", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, filters, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
    loadData(1, debouncedSearch, filters);
  }, [debouncedSearch, filters, pageSize]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Fetch the full district list for whichever state is selected in the Filters panel
  // (loops past the server's page_size=100 cap), so districts beyond the first alphabetical
  // page of the global prefetch are still findable once a state is chosen.
  useEffect(() => {
    const stateId = draftFilters.state_id;
    if (!stateId || filterDistrictsByState[stateId]) return;
    let cancelled = false;
    (async () => {
      let page = 1;
      let all = [];
      while (true) {
        const res = await fetchMasterData("districts", { page, page_size: 100, search: "", state_id: stateId });
        all = all.concat(res.data);
        if (cancelled || page >= res.total_pages) break;
        page += 1;
      }
      if (!cancelled) {
        setFilterDistrictsByState((prev) => ({ ...prev, [stateId]: all }));
      }
    })();
    return () => { cancelled = true; };
  }, [draftFilters.state_id, filterDistrictsByState]);

  // States + districts: Filters panel options, form dropdowns and the
  // Hub/Spoke hint — load once.
  useEffect(() => {
    fetchMasterData("states", { page: 1, page_size: 100, search: "" })
      .then((res) => setStates(res.data || []))
      .catch((err) => console.error("Failed to load states", err));
    fetchMasterData("districts", { page: 1, page_size: 100, search: "" })
      .then((res) => setDistricts(res.data || []))
      .catch((err) => console.error("Failed to load districts", err));
  }, []);

  // The states/districts/blocks lists are already role-scoped server-side
  // (SPL: own states, PL: own districts), so they need no further filtering
  // and every listed row is within the caller's area.
  const modalStates = states;

  const canDeleteItem = () => ["ADMIN", "SPL", "PL"].includes(role);

  const getDistrictName = (item) => {
    return item.district_name || "—";
  };

  const getStateName = (item) => {
    return item.state_name || "—";
  };

  const filteredDistricts = (() => {
    if (!formStateId) return [];
    return districts.filter((d) => d.state_id === Number(formStateId));
  })();

  const openCreate = () => {
    setEditing(null);
    setFormName("");
    setFormStateId("");
    setFormDistrictId("");
    setView("form");
  };

  const openEdit = async (item) => {
    // A direct click already has the full item — mark the URL-restore effect
    // as satisfied so it doesn't also redundantly re-fetch this same item.
    hasRestoredFromUrl.current = true;
    setEditing(item);
    setFormName(item.name);
    setFormDistrictId(item.district_id);
    // The list row carries state_id directly; the single-item GET (used to
    // restore from a URL refresh) does not. Try the already-loaded districts
    // list first (fast path), but that list is capped at page_size=100 and
    // may not contain this district — fall back to a direct by-id lookup
    // rather than silently leaving the State field blank.
    let stateId = item.state_id || districts.find((d) => d.id === Number(item.district_id))?.state_id || "";
    if (!stateId && item.district_id) {
      try {
        const res = await fetchDistrictById(item.district_id);
        stateId = res.data?.state_id || "";
      } catch {
        // leave blank — user can still pick the state manually
      }
    }
    setFormStateId(stateId);
    setView("form", { edit: item.id });
  };

  // Restore edit view from URL on refresh — re-fetch the single block (its
  // GET returns { success, data: { id, name, district_id, is_accelerated } },
  // no state_id) and feed the unwrapped item through the normal openEdit path.
  const hasRestoredFromUrl = useRef(false);
  useEffect(() => {
    if (hasRestoredFromUrl.current) return;
    if (view === "form" && urlEditId) {
      hasRestoredFromUrl.current = true;
      fetchBlockById(urlEditId)
        .then((res) => openEdit(res.data))
        .catch(() => setView("list"));
    } else if (view === "form" && !urlEditId) {
      hasRestoredFromUrl.current = true;
      openCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, urlEditId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formStateId) {
      setToast({ message: "Please select a state", type: "error" });
      return;
    }
    if (!formDistrictId) {
      setToast({ message: "Please select a district", type: "error" });
      return;
    }
    const trimmedName = formName.trim();
    if (!/\p{L}/u.test(trimmedName)) {
      setToast({ message: "Name must contain at least one letter (not just numbers, spaces, or symbols)", type: "error" });
      return;
    }
    try {
      if (editing) {
        await updateBlock(editing.id, trimmedName, Number(formDistrictId), true);
        setView("list");
      } else {
        await createBlock(trimmedName, Number(formDistrictId), true);
        setView("list");
      }
      loadData(currentPage, debouncedSearch);
    } catch (err) {
      console.error("Save failed", err);
      setToast({ message: apiErrorMessage(err, "Operation failed. Please try again."), type: "error" });
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteBlock(id);
      setShowDeleteConfirm(null);
      setSelectedIds((prev) => prev.filter((sid) => sid !== id));
      if (view !== "list") setView("list");
      loadData(currentPage, debouncedSearch);
    } catch (err) {
      console.error("Delete failed", err);
      setShowDeleteConfirm(null);
      const msg = err.response?.data?.detail || "Failed to delete block. Please try again.";
      setToast({ message: msg, type: "error" });
    }
  };

  /* Bulk Selection Handlers */
  const handleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const pageIds = data.filter((item) => canDeleteItem(item)).map((item) => item.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
    }
  };

  const clearSelection = () => setSelectedIds([]);

  const handleBulkDelete = async () => {
    if (bulkLoading) return;
    setBulkLoading(true);
    try {
      const res = await bulkAction("blocks", "delete", selectedIds);
      setShowBulkConfirm(false);
      setSelectedIds([]);
      setToast({
        message: res.message || `Successfully deleted ${res.processed} block(s).`,
        type: "success",
      });
      loadData(currentPage, debouncedSearch);
    } catch (err) {
      console.error("Bulk delete failed", err);
      const msg =
        err.response?.data?.detail || "Bulk delete failed. Please try again.";
      setToast({ message: msg, type: "error" });
      setShowBulkConfirm(false);
    } finally {
      setBulkLoading(false);
    }
  };

  /* Bulk Upload Handlers */
  const openUploadModal = () => {
    setUploadFile(null);
    setUploadResult(null);
    setShowUploadModal(true);
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f && !f.name.endsWith(".xlsx")) {
      setToast({ message: "Only .xlsx files are accepted", type: "error" });
      return;
    }
    setUploadFile(f || null);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!uploadFile || uploadLoading) return;
    setUploadLoading(true);
    setUploadResult(null);
    try {
      const res = await bulkUpload("blocks", uploadFile);
      setUploadResult(res);
      if (res.success) {
        setToast({ message: res.message || `Inserted ${res.inserted} block(s)`, type: "success" });
        loadData(currentPage, debouncedSearch);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Upload failed";
      setUploadResult({ success: false, errors: [], message: detail });
      setToast({ message: detail, type: "error" });
    } finally {
      setUploadLoading(false);
    }
  };

  const downloadTemplate = () => {
    const { downloadXlsxTemplate } = require("../services/xlsxTemplate");
    downloadXlsxTemplate("blocks_template.xlsx", ["name", "district_name"], [["Sadar", "Lucknow"], ["Haveli", "Pune"], ["Rural", "Nagpur"]]);
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <div className="p-6 space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-4 animate-pulse">
              <div className="w-5 h-5 bg-[#e5e7eb] rounded" />
              <div className="w-8 h-4 bg-[#e5e7eb] rounded" />
              <div className="flex-1 h-4 bg-[#e5e7eb] rounded" />
              <div className="w-24 h-4 bg-[#e5e7eb] rounded" />
              <div className="w-24 h-4 bg-[#e5e7eb] rounded" />
              <div className="w-20 h-4 bg-[#e5e7eb] rounded" />
            </div>
          ))}
        </div>
      );
    }
    if (data.length === 0) {
      return (
        <div className="text-center py-16 px-5">
          <div className="w-16 h-16 bg-[#f3f4f6] rounded-lg flex items-center justify-center mx-auto mb-4">
            <HiOutlineLocationMarker className="text-2xl text-[#8b949e]" />
          </div>
          <p className="m-0 text-sm font-medium text-[#57606a]">
            {search ? "No blocks match your search." : "No blocks found."}
          </p>
          <p className="m-0 text-xs text-[#8b949e] mt-1">
            {search ? "Try a different search term." : "Create your first block to get started."}
          </p>
        </div>
      );
    }

    const pageIds = data.map((item) => item.id);
    const allSelected =
      pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

    return (
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
            {canManageBlocks && (
              <th className="w-12 text-center py-4 px-3">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  className="w-4 h-4 rounded border-[#d0d5dd] text-[#0078d4] focus:ring-[#0078d4] cursor-pointer accent-[#0078d4]"
                  checked={allSelected}
                  onChange={handleSelectAll}
                  title="Select all"
                />
              </th>
            )}
            <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a] w-[60px]">#</th>
            <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Name</th>
            <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">District</th>
            <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">State</th>
            <th className="text-left py-4 px-6 text-xs font-medium text-[#57606a]">Type</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#e5e7eb]">
          {data.map((item, idx) => {
            const isSelected = selectedIds.includes(item.id);
            return (
              <tr
                key={item.id}
                className={`group transition-colors duration-150 cursor-pointer ${isSelected
                    ? "bg-[#eff6ff] hover:bg-[#eff6ff]"
                    : "hover:bg-gray-50"
                  }`}
                onClick={() => openEdit(item)}
              >
                {canManageBlocks && (
                  <td className="w-12 text-center py-4 px-3" onClick={(e) => e.stopPropagation()}>
                    {canDeleteItem(item) ? (
                      <input
                        type="checkbox"
                        className="w-[18px] h-[18px] rounded border-[#d0d5dd] text-[#0078d4] focus:ring-[#0078d4] cursor-pointer accent-[#0078d4]"
                        checked={isSelected}
                        onChange={() => handleSelectRow(item.id)}
                      />
                    ) : null}
                  </td>
                )}
                <td className="py-4 px-6 text-[#8b949e] font-medium text-xs">{(currentPage - 1) * pageSize + idx + 1}</td>
                <td className="py-4 px-6">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#eff6ff] rounded-md flex items-center justify-center flex-shrink-0">
                      <HiOutlineLocationMarker className="text-sm text-[#0078d4]" />
                    </div>
                    <span className="font-medium text-[#24292f]">{item.name}</span>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-[#f3f4f6] text-[#57606a] rounded">
                    {getDistrictName(item)}
                  </span>
                </td>
                <td className="py-4 px-6">
                  <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-[#f3f4f6] text-[#57606a] rounded">
                    {getStateName(item)}
                  </span>
                </td>
                <td className="py-4 px-6">
                  {item.is_hub ? (
                    <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold bg-purple-50 text-purple-700 rounded">
                      Hub
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold bg-amber-50 text-amber-700 rounded">
                      Spoke
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="p-6">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* List View */}
      {view === "list" && (
      <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-x-auto">
        {/* Toolbar */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb]">
          <div className="relative flex items-center flex-1 max-w-sm">
            <HiOutlineSearch className="absolute left-3.5 text-[#8b949e] text-sm" />
            <input
              type="text"
              className="w-full py-2.5 pr-8 pl-10 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border placeholder:text-[#8b949e] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
              placeholder="Search blocks…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                className="absolute right-2 bg-transparent border-none text-[#8b949e] cursor-pointer p-1 rounded-md hover:text-[#57606a] hover:bg-[#f3f4f6] transition-colors"
                onClick={() => setSearch("")}
              >
                <HiOutlineX className="text-sm" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <FilterDropdown
              filters={[
                {
                  key: "state_id",
                  label: "State",
                  type: "select",
                  options: states.map((s) => ({ value: String(s.id), label: s.name })),
                },
                {
                  key: "district_id",
                  label: "District",
                  type: "select",
                  options: (
                    draftFilters.state_id
                      ? (filterDistrictsByState[draftFilters.state_id] || [])
                      : districts
                  ).map((d) => ({ value: String(d.id), label: d.name })),
                },
              ]}
              values={filters}
              onChange={(newFilters) => {
                setFilters(newFilters);
                setCurrentPage(1);
              }}
              onDraftChange={(draft) => setDraftFilters(draft)}
            />
            {canManageBlocks && (
              <>
                <button
                  className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#24292f] border border-[#d0d5dd] hover:bg-[#f3f4f6] hover:border-[#8b949e]"
                  onClick={openUploadModal}
                >
                  <HiOutlineUpload className="text-base" />
                  Bulk Upload
                </button>
                <button
                  className="inline-flex items-center gap-2 h-10 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]"
                  onClick={openCreate}
                >
                  <HiOutlinePlus className="text-base" />
                  Add Block
                </button>
              </>
            )}
          </div>
        </div>

        {/* Bulk Action Toolbar */}
        {canManageBlocks && selectedIds.length > 0 && (
          <div className="flex items-center justify-between px-6 py-3 bg-[#eff6ff] border-b border-[#d0d5dd] animate-[fadeIn_0.2s_ease-out]">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0078d4] text-white text-xs font-bold">
                {selectedIds.length}
              </span>
              <span className="text-sm font-medium text-[#24292f]">
                {selectedIds.length === 1 ? "item selected" : "items selected"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="inline-flex items-center gap-1.5 py-2 px-4 text-sm font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
                onClick={clearSelection}
              >
                Clear
              </button>
              <button
                className="inline-flex items-center gap-1.5 py-2 px-4 text-sm font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-red-600 text-white border-none hover:bg-red-700 shadow-sm"
                onClick={() => setShowBulkConfirm(true)}
              >
                <HiOutlineTrash className="text-sm" />
                Delete Selected
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {renderTableContent()}

        {/* Pagination */}
        {!loading && totalCount > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={(p) => loadData(p, debouncedSearch, filters)}
            onPageSizeChange={handlePageSizeChange}
            entityName="blocks"
          />
        )}
      </div>
      )}

      {/* Create/Edit Form */}
      {view === "form" && (
        <div className="bg-white rounded-xl border border-[#e5e7eb] w-full">
          {/* Form Header */}
          <div className="flex justify-between items-center px-6 py-5 border-b border-[#e5e7eb]">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setView("list")}
                className="w-8 h-8 flex items-center justify-center rounded-md text-[#57606a] hover:bg-[#f3f4f6] transition-all border-none bg-transparent cursor-pointer"
                title="Back"
              >
                <HiOutlineChevronLeft className="text-lg" />
              </button>
              <div>
                <h3 className="m-0 text-[15px] font-semibold text-[#24292f]">
                  {!canManageBlocks ? "Block Details" : editing ? "Edit Block" : "Add New Block"}
                </h3>
                <p className="m-0 mt-1 text-[13px] text-[#8b949e]">
                  {!canManageBlocks ? "View block information" : editing ? "Update block information" : "Create a new block entry"}
                </p>
              </div>
            </div>
            {canManageBlocks && editing && (
              <button onClick={() => setShowDeleteConfirm(editing)} className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">Delete</button>
            )}
          </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-5">
                <div>
                  <label htmlFor="block-state" className="block mb-2 text-sm font-medium text-[#24292f]">
                    State <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    value={String(formStateId || "")}
                    onChange={(id) => {
                      setFormStateId(id);
                      setFormDistrictId("");
                    }}
                    placeholder="Select a state"
                    fetchFn={scopedStatesFetchFn(role === "ADMIN", modalStates)}
                    cacheNamespace="block_state_id"
                    disabled={!canManageBlocks}
                    variant="form"
                  />
                </div>
                <div>
                  <label htmlFor="block-district" className="block mb-2 text-sm font-medium text-[#24292f]">
                    District <span className="text-red-500">*</span>
                  </label>
                  {role === "PL" ? (
                    <select
                      id="block-district"
                      className="w-full py-2.5 px-4 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10 disabled:bg-[#f3f4f6] disabled:text-[#8b949e] disabled:cursor-not-allowed"
                      value={formDistrictId}
                      onChange={(e) => setFormDistrictId(e.target.value)}
                      required
                      disabled={!formStateId || !canManageBlocks}
                    >
                      <option value="">{formStateId ? "Select a district" : "Select a state first"}</option>
                      {filteredDistricts.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  ) : (
                    <SearchableDropdown
                      key={`blk-dist-${formStateId}`}
                      value={String(formDistrictId)}
                      onChange={(id) => setFormDistrictId(id)}
                      placeholder={formStateId ? "Select a district" : "Select a state first"}
                      fetchFn={(search, page = 1) => fetchMasterData("districts", {
                        page, page_size: 20,
                        state_id: Number(formStateId),
                        search,
                      })}
                      disabled={!formStateId || !canManageBlocks}
                      variant="form"
                    />
                  )}
                </div>
                <div>
                  <label htmlFor="block-name" className="block mb-2 text-sm font-medium text-[#24292f]">
                    Block Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="block-name"
                    className="w-full py-2.5 px-4 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
                    type="text"
                    placeholder="Enter block name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    disabled={!canManageBlocks}
                    required
                  />
                </div>
                <div>
                  <p className="m-0 text-xs text-[#8b949e]">
                    {(() => {
                      const selectedDist = districts.find((d) => d.id === Number(formDistrictId));
                      if (!formDistrictId) return "Select a district to see the block type";
                      return selectedDist?.is_hub
                        ? "This block will be classified as Hub"
                        : "This block will be classified as Spoke";
                    })()}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
                <button
                  type="button"
                  className="py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
                  onClick={() => setView("list")}
                >
                  {canManageBlocks ? "Cancel" : "Back"}
                </button>
                {canManageBlocks && (
                  <button
                    type="submit"
                    className="py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe]"
                  >
                    {editing ? "Save Changes" : "Create Block"}
                  </button>
                )}
              </div>
            </form>
        </div>
      )}

      {/* Single Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[250] p-4" onClick={() => setShowDeleteConfirm(null)}>
          <div className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-sm shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4"><HiOutlineExclamation className="text-2xl text-red-600" /></div>
              <h3 className="m-0 text-lg font-semibold text-[#24292f]">Delete Block?</h3>
              <p className="m-0 mt-2 text-sm text-[#57606a] leading-relaxed">Are you sure you want to delete <strong>{showDeleteConfirm.name}</strong>? This action cannot be undone.</p>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
              <button className="flex-1 py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6]" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
              <button className="flex-1 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-red-600 text-white hover:bg-red-700 shadow-sm" onClick={() => handleDelete(showDeleteConfirm.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkConfirm && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-[250] p-4"
          onClick={() => !bulkLoading && setShowBulkConfirm(false)}
        >
          <div
            className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-sm shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Escape' && !bulkLoading) setShowBulkConfirm(false); }}
          >
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <HiOutlineExclamation className="text-2xl text-red-600" />
              </div>
              <h3 className="m-0 text-lg font-semibold text-[#24292f]">Confirm Bulk Delete</h3>
              <p className="m-0 mt-2 text-sm text-[#57606a] leading-relaxed">
                You are about to delete{" "}
                <span className="font-bold text-[#24292f]">{selectedIds.length}</span>{" "}
                block{selectedIds.length !== 1 ? "s" : ""}. This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
              <button
                className="flex-1 py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setShowBulkConfirm(false)}
                disabled={bulkLoading}
              >
                Cancel
              </button>
              <button
                className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleBulkDelete}
                disabled={bulkLoading}
              >
                {bulkLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Deleting…
                  </>
                ) : (
                  "Confirm Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Bulk Upload Modal */}
      {showUploadModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200] p-4"
          onClick={() => !uploadLoading && setShowUploadModal(false)}
        >
          <div
            className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-md shadow-[0_8px_24px_rgba(0,0,0,0.12)] animate-[fadeIn_0.2s_ease-out]"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Escape' && !uploadLoading) setShowUploadModal(false); }}
          >
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-5 border-b border-[#e5e7eb]">
              <div>
                <h3 className="m-0 text-[15px] font-semibold text-[#24292f]">Bulk Upload Blocks</h3>
                <p className="m-0 mt-1 text-[13px] text-[#8b949e]">Upload an Excel file (.xlsx) to import blocks</p>
              </div>
              <button
                className="w-8 h-8 flex items-center justify-center rounded-md text-[#8b949e] hover:text-[#57606a] hover:bg-[#f3f4f6] transition-all duration-200 border-none bg-transparent cursor-pointer"
                onClick={() => setShowUploadModal(false)}
                disabled={uploadLoading}
              >
                <HiOutlineX className="text-lg" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Template Download */}
              <div className="flex items-center gap-3 p-3 bg-[#eff6ff] rounded-md border border-[#d0d5dd]">
                <HiOutlineDocumentText className="text-lg text-[#0078d4] flex-shrink-0" />
                <div className="flex-1">
                  <p className="m-0 text-sm font-medium text-[#24292f]">Need a template?</p>
                  <p className="m-0 text-xs text-[#57606a] mt-0.5">Columns: name, district_name</p>
                </div>
                <button
                  className="inline-flex items-center gap-1 py-1.5 px-3 text-xs font-semibold rounded-lg cursor-pointer transition-all duration-200 bg-white text-[#0078d4] border border-[#d0d5dd] hover:bg-[#f3f4f6]"
                  onClick={downloadTemplate}
                >
                  <HiOutlineDownload className="text-sm" />
                  Download
                </button>
              </div>

              {/* File Input */}
              <div>
                <label className="block mb-2 text-sm font-medium text-[#24292f]">Excel File (.xlsx)</label>
                <div
                  className="relative border-2 border-dashed border-[#d0d5dd] rounded-md p-6 text-center hover:border-[#0078d4] transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <HiOutlineUpload className="text-2xl text-[#8b949e] mx-auto mb-2" />
                  {uploadFile ? (
                    <p className="m-0 text-sm font-medium text-[#24292f]">{uploadFile.name}</p>
                  ) : (
                    <p className="m-0 text-sm text-[#8b949e]">Click to select or drag a file</p>
                  )}
                  <p className="m-0 text-xs text-[#8b949e] mt-1">Max 5MB · Max 1000 rows</p>
                </div>
              </div>

              {/* Upload Result */}
              {uploadResult && (
                <div className={`p-4 rounded-md border ${uploadResult.success
                    ? "bg-[#f0fdf4] border-[#bbf7d0]"
                    : "bg-red-50 border-red-200"
                  }`}>
                  <p className={`m-0 text-sm font-semibold ${uploadResult.success ? "text-[#16a34a]" : "text-red-700"
                    }`}>
                    {uploadResult.success
                      ? ` Inserted ${uploadResult.inserted} record(s)`
                      : uploadResult.message || "Validation failed"}
                  </p>
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="mt-3 max-h-40 overflow-y-auto space-y-1">
                      {uploadResult.errors.map((err, i) => (
                        <p key={i} className="m-0 text-xs text-red-600">
                          Row {err.row}: {err.message}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#e5e7eb] bg-[#f6f8fa] rounded-b-lg">
              <button
                className="py-2.5 px-5 text-sm font-semibold rounded-md cursor-pointer transition-all duration-200 bg-white text-[#57606a] border border-[#d0d5dd] hover:bg-[#f3f4f6] disabled:opacity-50"
                onClick={() => setShowUploadModal(false)}
                disabled={uploadLoading}
              >
                {uploadResult?.success ? "Close" : "Cancel"}
              </button>
              {!uploadResult?.success && (
                <button
                  className="inline-flex items-center justify-center gap-2 py-2.5 px-5 text-sm font-semibold border-none rounded-md cursor-pointer transition-all duration-200 bg-[#0078d4] text-white hover:bg-[#106ebe] disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleUpload}
                  disabled={!uploadFile || uploadLoading}
                >
                  {uploadLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Uploading…
                    </>
                  ) : (
                    <>
                      <HiOutlineUpload className="text-base" />
                      Upload
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Blocks;
